/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Button,
  Select,
  TextField,
  MenuItem,
  Stack,
  Fade,
  ClickAwayListener,
  Tooltip,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useApolloClient } from '@apollo/client';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import styles from './styles';
import { getAreaCodesList } from './Broadband.util';
import {
  TOOLTIP_TEXT_FIRST_PARA,
  TOOLTIP_TEXT_SECOND_PARA,
  VALIDATION_TEXT,
  USE_PHONENUMBER_TEXT,
} from '../shared/Constants';

const BroadbandPrequalPhoneInput = ({
  prequalHandler,
  fontColor,
  setPrequalByPhone,
  setPrequalFailed,
  PrequalContentAlign,
}) => {
  const theme = useTheme();
  const [areaCode, setAreaCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [areaCodes, setAreaCodes] = useState([]);
  const [toolTipOpen, setToolTipOpen] = useState(false);
  const md = useMediaQuery(theme.breakpoints.up('md'));

  const graphql = useApolloClient();
  // eslint-disable-next-line no-unused-vars
  let toolTipTimeout = null;
  useEffect(() => {
    (async function getAreaCodes() {
      try {
        // setPrequalFailed(true);
        const data = await getAreaCodesList(graphql);
        setAreaCodes(data);
      } catch (err) {
        setAreaCodes([]);
      }
    })();
  }, []);
  const phoneNumberChangeHandler = e => {
    e.preventDefault();
    if (isNaN(e.target.value) === true) {
      setPhoneNumberError(true);
      setDisableButton(true);
    }
    if (isNaN(e.target.value) === false) {
      if (e.target.value.length > 4) {
        if (`${areaCode}`.length !== 0) {
          setPhoneNumberError(false);
          setPhoneNumber(e.target.value);
          setDisableButton(false);
        } else {
          setPhoneNumberError(false);
          setPhoneNumber(e.target.value);
        }
      } else {
        setPhoneNumber(e.target.value);
        setPhoneNumberError(false);
        setDisableButton(true);
      }
    }
  };
  const handleTooltipOnOpen = () => {
    toolTipTimeout = setTimeout(() => {
      setToolTipOpen(false);
    }, 1500);
  };
  const checkAvailability = () => {
    prequalHandler(graphql, { areaCode, phoneNumber });
  };
  useEffect(() => {
    if (`${areaCode}`.length !== 0 && `${phoneNumber}`.length > 4) setDisableButton(false);
    else setDisableButton(true);

    if (toolTipTimeout) {
      clearTimeout(toolTipTimeout);
    }
  }, [areaCode, phoneNumber]);
  return PrequalContentAlign === 'Center' && md ? (
    <Grid
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '2rem',
        pl: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ marginTop: '-24px', marginRight: '16px' }}>
          <Select
            sx={[
              styles(theme).PrequalAreaCode,
              {
                ...(!areaCode
                  ? styles(theme).AreaCodeNullColor
                  : styles(theme).AreaCodeFilledColor),
              },
            ]}
            // eslint-disable-next-line react/no-unstable-nested-components
            IconComponent={props => <KeyboardArrowDownIcon {...props} />}
            disableUnderline
            labelId='prequal-area-code-label'
            id='prequal-area-code'
            label='Prefix'
            value={areaCode}
            error={phoneNumberError}
            displayEmpty
            onChange={e => {
              setAreaCode(e.target.value);
            }}
            renderValue={selected => {
              if (selected.length === 0) return 'Prefix';
              return selected;
            }}
          >
            {areaCodes.map(ele => (
              <MenuItem key={ele.AREA_CODE} value={ele.AREA_CODE}>
                {ele.AREA_CODE}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <TextField
            sx={styles(theme).PrequalPhoneNumber}
            placeholder='Phone number'
            margin='normal'
            variant='outlined'
            error={phoneNumberError}
            inputProps={{ maxLength: 7 }}
            onChange={e => phoneNumberChangeHandler(e)}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              classes: { input: styles(theme).TextFieldPlaceholder },
              endAdornment: (
                <ClickAwayListener onClickAway={() => setToolTipOpen(false)}>
                  <Tooltip
                    title={
                      <div>
                        <span style={styles(theme).toolTipText}>{TOOLTIP_TEXT_FIRST_PARA}</span>
                        <br />
                        <br />
                        <span style={styles(theme).toolTipText}>{TOOLTIP_TEXT_SECOND_PARA}</span>
                      </div>
                    }
                    sx={styles(theme).PhoneNumberTextFieldTooltip}
                    TransitionComponent={Fade}
                    open={toolTipOpen}
                    placement='right'
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={styles(theme).PhoneNumberTextFieldIconImage}
                      onMouseEnter={() => {
                        setToolTipOpen(true);
                      }}
                      onClick={() => {
                        setToolTipOpen(true);
                      }}
                      onMouseOut={() => {
                        handleTooltipOnOpen();
                      }}
                    />
                  </Tooltip>
                </ClickAwayListener>
              ),
            }}
          />
          {phoneNumberError ? (
            <Typography sx={styles(theme).PhoneValidity} style={{ color: fontColor }}>
              {VALIDATION_TEXT.phoneNumberValidation}
            </Typography>
          ) : (
            <Stack direction='row'>
              <Typography sx={[styles(theme).useEircode, { color: fontColor }]}>
                {USE_PHONENUMBER_TEXT.text}
              </Typography>{' '}
              &nbsp;
              <Typography
                sx={[styles(theme).useEircode, { textDecoration: 'underline', color: fontColor }]}
                onClick={() => {
                  setPrequalByPhone(false);
                  setPrequalFailed(false);
                }}
              >
                {USE_PHONENUMBER_TEXT.urlText}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
      <Box sx={{ marginLeft: '16px' }}>
        <Button
          sx={{ ...styles(theme).PrequalPhoneNumberSubmitButton, marginTop: '-24px' }}
          disableRipple
          id='check_via_phoneNumber'
          variant='contained'
          size='large'
          color='primary'
          type='button'
          disabled={disableButton}
          onClick={() => {
            checkAvailability();
          }}
          aria-label='Check availability'
        >
          Check availability
        </Button>
      </Box>
    </Grid>
  ) : (
    <Grid container sx={styles(theme).prequalInputContainer}>
      <Grid item container xs={12} md={8} sx={styles(theme).CodeandPhoneContainer}>
        <Grid item xs={3} sm={3} md={3}>
          <Select
            sx={[
              styles(theme).PrequalAreaCode,
              {
                ...(!areaCode
                  ? styles(theme).AreaCodeNullColor
                  : styles(theme).AreaCodeFilledColor),
              },
            ]}
            // eslint-disable-next-line react/no-unstable-nested-components
            IconComponent={props => <KeyboardArrowDownIcon {...props} />}
            disableUnderline
            labelId='prequal-area-code-label'
            id='prequal-area-code'
            label='Prefix'
            value={areaCode}
            error={phoneNumberError}
            displayEmpty
            onChange={e => {
              setAreaCode(e.target.value);
            }}
            renderValue={selected => {
              if (selected.length === 0) return 'Prefix';
              return selected;
            }}
          >
            {areaCodes.map(ele => (
              <MenuItem key={ele.AREA_CODE} value={ele.AREA_CODE}>
                {ele.AREA_CODE}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={8} sm={7} md={8}>
          <TextField
            sx={styles(theme).PrequalPhoneNumber}
            placeholder='Phone number'
            margin='normal'
            variant='outlined'
            error={phoneNumberError}
            inputProps={{ maxLength: 7 }}
            onChange={e => phoneNumberChangeHandler(e)}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              classes: { input: styles(theme).TextFieldPlaceholder },
              endAdornment: (
                <ClickAwayListener onClickAway={() => setToolTipOpen(false)}>
                  <Tooltip
                    title={
                      <div>
                        <span style={styles(theme).toolTipText}>{TOOLTIP_TEXT_FIRST_PARA}</span>
                        <br />
                        <br />
                        <span style={styles(theme).toolTipText}>{TOOLTIP_TEXT_SECOND_PARA}</span>
                      </div>
                    }
                    sx={styles(theme).PhoneNumberTextFieldTooltip}
                    TransitionComponent={Fade}
                    open={toolTipOpen}
                    placement='right'
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={styles(theme).PhoneNumberTextFieldIconImage}
                      onMouseEnter={() => {
                        setToolTipOpen(true);
                      }}
                      onClick={() => {
                        setToolTipOpen(true);
                      }}
                      onMouseOut={() => {
                        handleTooltipOnOpen();
                      }}
                    />
                  </Tooltip>
                </ClickAwayListener>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} sm={6} md={4} sx={{ mt: { xs: 3, md: 0 }, width: '90%' }}>
        <Button
          sx={styles(theme).PrequalPhoneNumberSubmitButton}
          disableRipple
          id='check_via_phoneNumber'
          variant='contained'
          size='large'
          color='primary'
          type='button'
          disabled={disableButton}
          onClick={() => {
            checkAvailability();
          }}
          aria-label='Check availability'
        >
          Check availability
        </Button>
      </Grid>
      <Grid item xs={12} sx={{ mt: 0.5 }}>
        {phoneNumberError ? (
          <Typography variant='body2' sx={styles(theme).PhoneValidity} style={{ color: fontColor }}>
            {VALIDATION_TEXT.phoneNumberValidation}
          </Typography>
        ) : (
          <Stack direction='row'>
            <Typography variant='body2' sx={[styles(theme).useEircode, { color: fontColor }]}>
              {USE_PHONENUMBER_TEXT.text}
            </Typography>{' '}
            &nbsp;
            <Typography
              variant='body2'
              sx={[styles(theme).useEircode, { textDecoration: 'underline', color: fontColor }]}
              onClick={() => {
                setPrequalByPhone(false);
                setPrequalFailed(false);
              }}
            >
              {USE_PHONENUMBER_TEXT.urlText}
            </Typography>
          </Stack>
        )}
      </Grid>
    </Grid>
  );
};

export default BroadbandPrequalPhoneInput;
